import Env from '@lib/Env';

import { Module } from 'vuex';
import { IRootState, IActorState } from '@types';

import { Actor } from '@types';

import {
    ActorModel,
    ActorPhotoModel,
    ActorPortfolioModel,
    ActorVideoModel,
    ActorVideoRequestModel,
    ActorVideoGalleryModel,
} from '@model';

import { ActorsApi, StorageApi } from '@/api';
import { VideoApi } from '@/api/Admin/VideoApi';
import video from '@/router/video';

const store: Module<IActorState, IRootState> = {
    state: {
        actor: <ActorModel>null,
        actorAccessToken: '',
        showCamera: false,
        imageSliderIndex: 0,
        videoGallery: null,
        videoPlaying: null,
        selectedVideoRequest: null,
        videoRequestSelectedSection: null,
    },

    mutations: {
        setActor: (state, actor: ActorModel) => (state.actor = actor),
        setActorAccessToken: (state, accessToken: string) => (state.actorAccessToken = accessToken),
        setActorGallery: (state, records: ActorPhotoModel[]) => (state.actor.photos = records),
        setVideoRequestList: (state, records: ActorVideoRequestModel[]) => (state.actor.video_requests = records),
        setActorPortfolio: (state, records: ActorPortfolioModel[]) => (state.actor.portfolio = records),
        showCamera: (state, show) => (state.showCamera = show),
        setImageSliderIndex: (state, index) => (state.imageSliderIndex = index),
        setVideoByHash: (state, video: any) => {
            const index = state.actor.videos.findIndex((vid) => video.hash == vid.hash);
            if (index >= 0) {
                state.actor.videos[index] = video;
            }
        },
        setVideoRequests: (state, record: ActorVideoRequestModel) => {
            const index = state.actor.videoRequests.findIndex((video_request: any) => record.id == video_request.id);
            if (index >= 0) {
                state.actor.videoRequests[index] = record;
            }
        },
        setActorVideoPlaying: (state, video) => (state.videoPlaying = video),
        setActorSelectedVideoRequest: (state, val) => (state.selectedVideoRequest = val),
        setActorVideoRequestSelectedSection: (state, val) => (state.videoRequestSelectedSection = val),
    },

    getters: {
        getActor: (state): ActorModel => state.actor,

        getActorAccessToken: (state): string => state.actorAccessToken,

        getIsSetActorAccessToken: (state, getters): boolean => !!getters.getActorAccessToken,

        getActorGallery(state, getters): ActorPhotoModel[] {
            return getters.getActor?.photos ?? [];
        },

        getActorVideoList(state, getters): ActorVideoRequestModel[] {
            return getters.getActor?.video_requests ?? [];
        },

        getActorPortfolio(state, getters): ActorPortfolioModel[] {
            return getters.getActor?.portfolio ?? [];
        },

        getActorRating(state, getters): Actor.Rating {
            return getters.getActor?.rating ?? {};
        },

        getImageSliderIndex: (state, getters) => state.imageSliderIndex,
        getShowCamera: (state, getters) => state.showCamera,

        // TODO: remove this, use absolute urls from api.
        getSampleMp3Url(state, getters, rootState, rootGetters) {
            return (file_id: string) => {
                return getters.getActorFileUrl(file_id);
            };
        },

        getActorFileUrl() {
            return (file_id: string) => {
                const lower = file_id.toLowerCase();
                const hasExtension = lower.includes('.') && !lower.endsWith('/');
                const isMp3 = lower.endsWith('.mp3');

                if (isMp3) {
                    return `${Env.services.storage.baseURL}/${encodeURI(file_id)}#t=0.001`;
                }

                if (!hasExtension) {
                    return `${Env.services.storage.baseURL}/mp3.php?file=${encodeURIComponent(file_id)}#t=0.001`;
                }

                return `${Env.services.storage.baseURL}/${encodeURI(file_id)}#t=0.001`;
            };

            // return (file_id: string) => {
            //     const lower = file_id.toLowerCase();
            //
            //     const isMp3WithoutExtension = lower.includes('/mp3/') || lower.endsWith('.mp3');
            //
            //     if (isMp3WithoutExtension) {
            //         return `${Env.services.storage.baseURL}/mp3.php?file=${encodeURIComponent(file_id)}#t=0.001`;
            //     }
            //
            //     return `${Env.services.storage.baseURL}/${encodeURI(file_id)}#t=0.001`;
            // };
        },

        getVideoRequestVideoFileUrl() {
            return (hash: string) => `${Env.services.admin.baseURL}/video/${hash}#t=0.001`;
        },

        getActorVideoGallery: (state, getters) => state.videoGallery,
        getActorVideoPlaying: (state, getters) => state.videoPlaying,
        getActorSelectedVideoRequest: (state, getters) => state.selectedVideoRequest,
        getActorVideoRequestSelectedSection: (state, getters) => state.videoRequestSelectedSection,
    },

    actions: {
        loadActor({ commit }, idOrToken?: number | string): Promise<ActorModel> {
            return new Promise((resolve, reject) => {
                const include = ['personalData', 'photos', 'portfolio', 'rating'];
                ((idOrToken && ActorsApi.load(idOrToken, { include })) || ActorsApi.loadMe())
                    .then((response) => {
                        const actorModel = new ActorModel(response.data);
                        commit('setActor', actorModel);
                        resolve(actorModel);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadActorFile({ commit }, url: string): Promise<any> {
            return new Promise((resolve, reject) => {
                StorageApi.loadActorFile(url)
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadActorPortfolio({ commit, getters }, idOrToken?: number | string): Promise<ActorPortfolioModel[]> {
            return new Promise((resolve, reject) => {
                ActorsApi.loadPortfolio(idOrToken || getters.getActor.id)
                    .then(({ data }) => {
                        getters.getActor.portfolio = data.portfolio.map(
                            (i: Actor.Portfolio) => new ActorPortfolioModel(i)
                        );
                        resolve(getters.getActor.portfolio);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        // TODO: obsolete
        loadActorVideoList({ commit, getters }, idOrToken?: number | string): Promise<ActorVideoRequestModel[]> {
            return new Promise((resolve, reject) => {
                VideoApi.loadVideoList({
                    actor_id: idOrToken || getters.getActor.id,
                })
                    .then((response) => {
                        const records = [];
                        for (var i in response.data.records) {
                            records.push(new ActorVideoRequestModel(response.data.records[i]));
                        }
                        commit('setVideoRequestList', records);
                        resolve(records);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        async loadActorVideoRequests({ commit, getters }, { id, token, include }): Promise<ActorPortfolioModel[]> {
            const { data } = await ActorsApi.loadVideoRequests(id ?? token ?? getters.getActor.id, { include });
            return (getters.getActor.videoRequests = data.video_requests.map(
                (i: Actor.VideoRequest) => new ActorVideoRequestModel(i)
            ));
        },

        loadActorGallery({ commit, getters }, idOrToken?: number | string): Promise<ActorPhotoModel[]> {
            return new Promise((resolve, reject) => {
                ActorsApi.loadGallery(idOrToken || getters.getActor.id)
                    .then((response) => {
                        getters.getActor.photos = response.data.photos.map((i: Actor.Photo) => new ActorPhotoModel(i));
                        commit('setImageSliderIndex', Math.max(0, getters.getActor.photos.length - 1));
                        resolve(getters.getActor.photos);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        loadActorVideoGallery({ commit, getters }, idOrToken?: number | string): Promise<ActorVideoGalleryModel[]> {
            return new Promise((resolve, reject) => {
                ActorsApi.loadVideoGallery(idOrToken || getters.getActor.id)
                    .then((response) => {
                        const records = [
                            // { id: 1, file_id: 'video1.mp4', name: 'video1.mp4', order: 0 },
                            // { id: 2, file_id: 'video2.mp4', name: 'video2.mp4', order: 1 },
                            // { id: 3, file_id: 'video3.mp4', name: 'video3.mp4', order: 2 },
                            // { id: 4, file_id: 'video4.mp4', name: 'video4.mp4', order: 4 },
                        ];
                        for (let i in response.data.records) {
                            records.push(new ActorVideoGalleryModel(response.data.records[i]));
                        }
                        getters.getActor.videoGallery = records;
                        resolve(records);
                    })
                    .catch((err) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        savePhotoToActorGallery({ commit, getters }, file_id: string) {
            const actor = <ActorModel>getters.getActor;

            actor.addPhoto(
                new ActorPhotoModel({
                    id: null,
                    file_id,
                    poradie: null,
                })
            );

            return actor.saveGallery();
        },

        saveActorGallery({ commit, getters }, images) {
            const actor = <ActorModel>getters.getActor;
            return actor.saveGallery();
        },

        removePhotoFromGallery({ commit, dispatch, getters }, file_id: string) {
            const actor = <ActorModel>getters.getActor;
            actor.removePhotoByFileId(file_id);

            return new Promise((resolve, reject) => {
                actor
                    .saveGallery()
                    .then((response: any) => {
                        dispatch('loadActorGallery')
                            .then((response) => {
                                commit('setImageSliderIndex', 0);
                                resolve(response.data);
                            })
                            .catch((err) => {});
                    })
                    .catch((err: any) => {
                        Env.log.error(err);
                        reject(err);
                    });
            });
        },

        uploadActorPhoto({ getters }, { image, name }) {
            return StorageApi.uploadActorFile(image, name, getters.getActor, 'Photos');
        },

        uploadActorAudio({ getters }, { file, name }) {
            return StorageApi.uploadActorFile(file, name, getters.getActor, 'Portfolio');
        },

        uploadActorVideo({ getters }, { file, name }) {
            return StorageApi.uploadActorFile(file, name, getters.getActor, 'Videos');
        },

        startActorImpersonation({ state }, actor: ActorModel) {
            return ActorsApi.startImpersonation(actor);
        },

        stopActorImpersonation() {
            return ActorsApi.stopImpersonation();
        },
    },
};

export default store;
